<template>
    <FormControl
        required
        label="Your name or business name"
        :error="
            errors?.getValidationError(
                RequestBodyKeyEnum.App_UpdateCompany_DisplayName,
            )
        "
    >
        <TextInput
            :name="RequestBodyKeyEnum.App_UpdateCompany_DisplayName"
            :value="company.displayName"
            required
            @input="
                emits('onDisplayNameUpdated', { value: $event.target.value })
            "
        />
    </FormControl>

    <div>
        <FormControl
            required
            label="Booking page URL"
            :error="
                errors?.getValidationError(
                    RequestBodyKeyEnum.App_UpdateCompany_Slug,
                )
            "
        >
            <TextInput
                :prefix="bookingFormDomain"
                :name="RequestBodyKeyEnum.App_UpdateCompany_Slug"
                :value="company.slug"
                required
                @input="emits('onSlugUpdated', { value: $event.target.value })"
            />
        </FormControl>
        <div
            v-if="showWarning"
            class="text-sm"
        >
            <Alert
                title="ATTENTION!"
                type="warning"
            >
                Keep in mind if you update your URL the previous one will stop
                working.
            </Alert>
        </div>
    </div>

    <div v-if="company.logoImage">
        <FormControl label="Current logo">
            <Image
                :src="company.logoImage.url"
                class="max-h-36 w-auto mx-auto border border-stone-200"
            />
        </FormControl>
    </div>
    <FormControl
        label="Logo or photo"
        :error="
            errors?.getValidationError(
                RequestFileKeyEnum.App_UpdateCompany_LogoImage,
            )
        "
    >
        <FileInput
            :name="RequestFileKeyEnum.App_UpdateCompany_LogoImage"
            :accept="CompanyLogoFileData.allowedMimeTypes"
            :maxSizeInBytes="CompanyLogoFileData.maxFileSizeInBytes"
            :maxSize="1"
            iconName="card-image"
            @onPreviewUrlUpdated="handleLogoImageUpdated($event.value)"
        />
    </FormControl>

    <div v-if="company.coverImage">
        <FormControl label="Current cover image">
            <Image
                :src="company.coverImage.url"
                class="max-h-36 w-auto mx-auto border border-stone-200"
            />
        </FormControl>
    </div>
    <FormControl
        label="Cover image"
        :error="
            errors?.getValidationError(
                RequestFileKeyEnum.App_UpdateCompany_CoverImage,
            )
        "
    >
        <FileInput
            :name="RequestFileKeyEnum.App_UpdateCompany_CoverImage"
            :accept="CompanyCoverFileData.allowedMimeTypes"
            :maxSizeInBytes="CompanyCoverFileData.maxFileSizeInBytes"
            iconName="card-image"
            @onPreviewUrlUpdated="handleCoverImageUpdated($event.value)"
        />
    </FormControl>

    <slot name="extraFields" />
</template>

<script setup lang="ts">
    import { type PropType } from 'vue'
    import FormControl from '@/Shared/Component/Forms/FormControl.vue'
    import TextInput from '@/Shared/Component/Forms/TextInput.vue'
    import FileInput from '@/Shared/Component/Forms/FileInput.vue'
    import Company from '@/App/Company/Entity/Company'
    import Alert from '@/Shared/Component/Alert.vue'
    import { UrlFactory } from '@/Shared/Url/UrlFactory'
    import {
        CompanyCoverFileData,
        CompanyLogoFileData,
    } from '@/Shared/AutoGenerated/Main'
    import ApiFormErrors from '@/Shared/Component/Forms/ApiFormErrors'
    import {
        RequestBodyKeyEnum,
        RequestFileKeyEnum,
    } from '@/Shared/AutoGenerated/RequestFieldEnum'
    import Image from '@/Shared/Component/Image.vue'

    const emits = defineEmits<{
        onDisplayNameUpdated: [{ value: string }]
        onSlugUpdated: [{ value: string }]
        onLogoUpdated: [{ value: string | null }]
        onCoverUpdated: [{ value: string | null }]
    }>()

    const bookingFormDomain =
        new UrlFactory()
            .makeBookingFormRootRedirectUrl()
            .replace('http://', '')
            .replace('https://', '') + '/'

    const props = defineProps({
        company: {
            type: Object as PropType<Company>,
            required: true,
        },
        errors: {
            type: Object as PropType<ApiFormErrors>,
            required: true,
        },
        showWarning: {
            type: Boolean,
            default: true,
        },
    })

    const handleLogoImageUpdated = (previewUrl: string | null) => {
        emits('onLogoUpdated', { value: previewUrl })
    }

    const handleCoverImageUpdated = (previewUrl: string | null) => {
        emits('onCoverUpdated', { value: previewUrl })
    }
</script>
